/**
 * My Agents Dashboard - Manage custom AI agents
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  Bot, Plus, Settings, Trash2, MessageSquare, 
  FileText, Palette, BarChart3, ExternalLink,
  Store, ShoppingCart, Briefcase, Users,
  CheckCircle, Clock, AlertCircle
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIT_ICONS = {
  small_business: Store,
  online_seller: ShoppingCart,
  freelancer: Briefcase,
  consultant: Users
};

const STATUS_CONFIG = {
  draft: { icon: Clock, color: 'text-yellow-400', bg: 'bg-yellow-500/20', label: 'Draft' },
  active: { icon: CheckCircle, color: 'text-green-400', bg: 'bg-green-500/20', label: 'Active' },
  paused: { icon: AlertCircle, color: 'text-orange-400', bg: 'bg-orange-500/20', label: 'Paused' }
};

export default function MyAgents() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState(null);

  useEffect(() => {
    if (user && token) {
      fetchAgents();
    }
  }, [user, token]);

  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${API}/agent-builder/my-agents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgents(response.data.agents || []);
    } catch (err) {
      console.error('Failed to fetch agents:', err);
    } finally {
      setLoading(false);
    }
  };

  const deleteAgent = async (agentId) => {
    if (!confirm('Are you sure you want to delete this agent?')) return;
    
    try {
      await axios.delete(`${API}/agent-builder/${agentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgents(agents.filter(a => a.id !== agentId));
      if (selectedAgent?.id === agentId) setSelectedAgent(null);
    } catch (err) {
      console.error('Failed to delete agent:', err);
    }
  };

  const activateAgent = async (agentId) => {
    try {
      await axios.post(`${API}/agent-builder/${agentId}/activate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchAgents();
    } catch (err) {
      console.error('Failed to activate agent:', err);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-24 pb-16 flex items-center justify-center">
        <div className="text-center">
          <Bot className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Sign in to view your agents</h2>
          <Button onClick={() => navigate('/auth')} className="bg-teal-500 hover:bg-teal-600 mt-4">
            Sign In
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 pt-24 pb-16">
      <div className="max-w-6xl mx-auto px-6">
        
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">My Agents</h1>
            <p className="text-zinc-400">Manage your custom AI business assistants</p>
          </div>
          <Button 
            onClick={() => navigate('/create-agent')}
            className="bg-teal-500 hover:bg-teal-600"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create New Agent
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12 text-zinc-500">Loading agents...</div>
        ) : agents.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-12 text-center"
          >
            <Bot className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">No Agents Yet</h2>
            <p className="text-zinc-400 mb-6 max-w-md mx-auto">
              Create your first custom AI agent with chatbot, templates, and branding tools tailored to your business.
            </p>
            <Button 
              onClick={() => navigate('/create-agent')}
              className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Agent
            </Button>
          </motion.div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Agents List */}
            <div className="lg:col-span-2 space-y-4">
              {agents.map((agent, index) => {
                const KitIcon = KIT_ICONS[agent.business_type] || Bot;
                const status = STATUS_CONFIG[agent.status] || STATUS_CONFIG.draft;
                const StatusIcon = status.icon;
                
                return (
                  <motion.div
                    key={agent.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    onClick={() => setSelectedAgent(agent)}
                    className={`bg-zinc-900/50 border rounded-xl p-5 cursor-pointer transition-all ${
                      selectedAgent?.id === agent.id 
                        ? 'border-teal-500' 
                        : 'border-zinc-800 hover:border-zinc-700'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div 
                          className="w-12 h-12 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: `${agent.primary_color}20` }}
                        >
                          <KitIcon className="w-6 h-6" style={{ color: agent.primary_color }} />
                        </div>
                        <div>
                          <h3 className="text-white font-semibold">{agent.name}</h3>
                          <p className="text-zinc-500 text-sm">{agent.kit_name}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded ${status.bg} ${status.color}`}>
                              <StatusIcon className="w-3 h-3" />
                              {status.label}
                            </span>
                            <span className="text-zinc-600 text-xs">
                              {agent.templates?.length || 0} templates
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {agent.status === 'draft' && (
                          <Button
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); activateAgent(agent.id); }}
                            className="bg-green-500/20 text-green-400 hover:bg-green-500/30"
                          >
                            Activate
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => { e.stopPropagation(); deleteAgent(agent.id); }}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Agent Details Panel */}
            <div className="lg:col-span-1">
              {selectedAgent ? (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 sticky top-24"
                >
                  <h3 className="text-white font-semibold mb-4">Agent Details</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="text-zinc-500 text-xs uppercase">Name</label>
                      <p className="text-white">{selectedAgent.name}</p>
                    </div>
                    
                    <div>
                      <label className="text-zinc-500 text-xs uppercase">Kit</label>
                      <p className="text-white">{selectedAgent.kit_name}</p>
                    </div>
                    
                    <div>
                      <label className="text-zinc-500 text-xs uppercase">Brand Voice</label>
                      <p className="text-white capitalize">{selectedAgent.brand_voice}</p>
                    </div>
                    
                    <div>
                      <label className="text-zinc-500 text-xs uppercase">Description</label>
                      <p className="text-zinc-300 text-sm">{selectedAgent.business_description}</p>
                    </div>

                    <div>
                      <label className="text-zinc-500 text-xs uppercase">Brand Color</label>
                      <div className="flex items-center gap-2 mt-1">
                        <div 
                          className="w-6 h-6 rounded"
                          style={{ backgroundColor: selectedAgent.primary_color }}
                        />
                        <span className="text-zinc-300 font-mono text-sm">{selectedAgent.primary_color}</span>
                      </div>
                    </div>
                    
                    <div className="border-t border-zinc-800 pt-4 space-y-2">
                      <Button
                        onClick={() => navigate(`/templates?agent=${selectedAgent.id}`)}
                        className="w-full bg-zinc-800 hover:bg-zinc-700 justify-start"
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        Use Templates
                      </Button>
                      
                      {selectedAgent.chatbot_id ? (
                        <Button
                          onClick={() => navigate(`/chat/${selectedAgent.chatbot_id}`)}
                          className="w-full bg-zinc-800 hover:bg-zinc-700 justify-start"
                        >
                          <MessageSquare className="w-4 h-4 mr-2" />
                          Open Chatbot
                        </Button>
                      ) : (
                        <Button
                          onClick={() => navigate('/chatbot-builder')}
                          className="w-full bg-teal-500/20 text-teal-400 hover:bg-teal-500/30 justify-start"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Create Chatbot
                        </Button>
                      )}
                    </div>
                  </div>
                </motion.div>
              ) : (
                <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 text-center">
                  <Bot className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                  <p className="text-zinc-500">Select an agent to view details</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
